import axios from "axios";
import { DataTable } from "simple-datatables";
import { handleRequestError, showAlert } from "../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  users: [],
  reportList: [],
  hasSearched: false,
  summary: {
    total_services: 0,
    total_products: 0,
    total_commissions: 0,
  },
  dataTableInstance: null,

  filters: {
    start_date: "",
    end_date: "",
    user_id: "",
  },

  fpStartDate: null,
  fpEndDate: null,

  async initCommissions() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      this.initFlatpickr();
      // Se loadSupportData retornar 403, o erro cairá diretamente no catch abaixo
      await this.loadSupportData();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  initFlatpickr() {
    const config = {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      allowInput: true,
      altInput: true,
      altFormat: "d/m/Y",
    };

    this.fpStartDate = flatpickr(this.$refs.startDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.start_date = dateStr;
      },
    });

    this.fpEndDate = flatpickr(this.$refs.endDateInput, {
      ...config,
      onChange: (selectedDates, dateStr) => {
        this.filters.end_date = dateStr;
      },
    });
  },

  // ================= DATA FETCHING =================
  // try/catch removido para o erro 403 subir para a função initCommissions
  async loadSupportData() {
    const res = await axios.get("/api/client/reports/commissions/support-data");
    this.users = res.data.users;
  },

  formatMoney(val) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  },

  // ================= REPORT GENERATION =================
  async loadReportTable() {
    if (!this.filters.start_date || !this.filters.end_date) {
      return showAlert(
        "Por favor, selecione as datas Inicial e Final para a apuração.",
        "warning",
      );
    }

    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/reports/commissions?${queryParams}`,
      );

      this.reportList = response.data.data;
      this.summary = response.data.summary;
      this.hasSearched = true;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#commissionsReportTable tbody");
      if (!tbody) return;

      tbody.innerHTML = this.reportList
        .map((row) => {
          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3 text-sm font-bold text-slate-800">${row.professional_name}</td>
            <td class="p-3 text-sm font-mono text-slate-700">${this.formatMoney(row.services_amount)}</td>
            <td class="p-3 text-sm font-mono text-slate-700">${this.formatMoney(row.products_amount)}</td>
            <td class="p-3 text-sm font-mono text-red-500">${row.deducted_fee > 0 ? "-" + this.formatMoney(row.deducted_fee) : "-"}</td>
            <td class="p-3 text-sm text-slate-600">
              <div class="text-xs">S: ${parseFloat(row.service_commission_rate)}%</div>
              <div class="text-xs">P: ${parseFloat(row.product_commission_rate)}%</div>
            </td>
            <td class="p-3 text-sm font-mono font-bold text-emerald-600">${this.formatMoney(row.net_commission)}</td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#commissionsReportTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          placeholder: "Pesquisar...",
          searchTitle: "Pesquisar na tabela",
          pageTitle: "Página {page}",
          perPage: "registros por página",
          noRows: "Nenhuma entrada encontrada",
          info: "Mostrando {start} a {end} de {rows} registros",
          noResults: "Nenhum resultado encontrado",
        },
      });
    } catch (error) {
      handleRequestError(error, "Erro ao processar apuração.");
    }
  },

  applyFilters() {
    this.loadReportTable();
  },

  exportToCSV() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados disponíveis para exportação.", "warning");

    let csvContent = "\uFEFF";
    csvContent +=
      "Profissional;Serviços;Produtos;Taxa Retida;% Comissões;Comissão Líquida\n";

    this.reportList.forEach((row) => {
      const ratesStr = `S:${row.service_commission_rate}% | P:${row.product_commission_rate}%`;
      const formattedFee =
        row.deducted_fee > 0 ? `-${row.deducted_fee.toFixed(2)}` : "0.00";

      csvContent += `"${row.professional_name}";"${row.services_amount.toFixed(2)}";"${row.products_amount.toFixed(2)}";"${formattedFee}";"${ratesStr}";"${row.net_commission.toFixed(2)}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `relatorio_comissoes_${this.filters.start_date}_a_${this.filters.end_date}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  async exportToPDF() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados.", "warning");

    try {
      const response = await axios.post(
        "/api/client/reports/request-download-token",
      );
      const downloadToken = response.data.download_token;

      const queryParams = new URLSearchParams(this.filters);
      queryParams.append("dtoken", downloadToken);

      window.open(
        `/client/reports/commissions/pdf?${queryParams.toString()}`,
        "_blank",
      );
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao solicitar autorização para impressão.",
      );
    }
  },
});
